import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import ChatHome from 'pages/Chatbot/ChatHome'
import ConversationSidebar from 'pages/Chatbot/ConversationSidebar'
import TermsAndConditions from 'pages/Chatbot/TermsAndConditions'
import Spinner from 'components/Spinner'
import Chatbot from 'components/Chatbot'
import useSearchParams from 'hooks/useSearchParams'
import useScreenSize from 'hooks/useScreenSize'
import { useSessionContext } from 'hooks/SessionContext'
import useChatbotTermsApi from 'hooks/useChatbotTermsApi'
import { ConversationMessages, GroupedConversations } from 'types/Chat'
import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'
import GA4 from 'helpers/ga4'
import ChatbotModel from 'models/Chatbot'

const AIChatbot = () => {
  const conversationIdFromUrl = useSearchParams('conversationId') || null
  const navigate = useNavigate()
  const showTermsInFooter = useSearchParams('showTerms') === 'true'
  const screenSize = useScreenSize()
  const { session } = useSessionContext()
  const { chatbotTermsUpToDate, hasLoadedTermsStatus } = useChatbotTermsApi()
  const dealerTier = session.dealerPlan.tier

  const [isCancelled, setIsCancelled] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [conversationMessage, setConversationMessage] = useState<ConversationMessages | null>(null)
  const [loadingConversation, setLoadingConversation] = useState(false)

  const [groupedConversations, setGroupedConversations] = useState<GroupedConversations>({})
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [nextToken, setNextToken] = useState<string | null>(null)
  const [remainingMessageCount, setRemainingMessageCount] = useState<number | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)

  const listConversations = useCallback(async (token: string | null = null, append = false) => {
    token ? setLoadingMore(true) : setLoading(true)
    setError(null)

    try {
      const { grouped, nextToken: newToken, nbrRemainingMessages } = await ChatbotModel.fetchConversations(token ?? undefined, dealerTier)
      setNextToken(newToken)
      setRemainingMessageCount(nbrRemainingMessages)
      if (append) {
        setGroupedConversations((prev) => {
          const next = { ...prev }
          for (const [month, items] of Object.entries(grouped)) {
            next[month] = [...(next[month] ?? []), ...items]
          }
          return next
        })
      } else {
        setGroupedConversations(grouped)
      }
    } catch (err) {
      setError('Failed to load conversations.')
      console.error('Error fetching conversations:', err)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [dealerTier])

  const handleToggleChats = useCallback((msg?: string) => {
    setIsChatOpen(prev => !prev)
    setMessage(msg || '')
  }, [])

  const handleToggleSidebar = useCallback(() => {
    GA4.sendEvent({ category: 'Chat Bot', action: 'Chat Bot Conversation Panel', label: isSidebarCollapsed ? 'Expand' : 'Collapse' })
    setIsSidebarCollapsed(prev => !prev)
  }, [isSidebarCollapsed])

  const getConversation = useCallback(async (conversationId: string) => {
    setLoadingConversation(true)
    try {
      const response = await ChatbotModel.getConversation(conversationId) as ConversationMessages | null
      if (response) {
        setConversationMessage(response)
      }
    } catch (err) {
      console.error('Error fetching conversation:', err)
    } finally {
      setLoadingConversation(false)
    }
  }, [])

  useEffect(() => {
    if (!hasLoadedTermsStatus || !chatbotTermsUpToDate) {
      return
    }
    listConversations()
  }, [hasLoadedTermsStatus, chatbotTermsUpToDate, listConversations])

  useEffect(() => {
    if (!hasLoadedTermsStatus || !chatbotTermsUpToDate || !conversationIdFromUrl) {
      return
    }
    setIsChatOpen(true)

    getConversation(conversationIdFromUrl)
  }, [hasLoadedTermsStatus, chatbotTermsUpToDate, conversationIdFromUrl, getConversation])

  useEffect(() => {
    setIsSidebarCollapsed(screenSize === 'small')
  }, [screenSize])

  const onSelectConversation = useCallback((id: string) => {
    setConversationMessage(null)
    getConversation(id)
    setIsChatOpen(true)
    if (screenSize === 'small') {
      setIsSidebarCollapsed(true)
    }
  }, [getConversation, screenSize])

  const openNewChat = useCallback(() => {
    setConversationMessage(null)
    setIsChatOpen(false)
    if (screenSize === 'small') {
      setIsSidebarCollapsed(true)
    }
    navigate(window.location.pathname, { replace: true })
  }, [navigate, screenSize])

  const onDeleteConversation = useCallback(async (id: string) => {
    try {
      const result = await ChatbotModel.deleteConversation(id)
      if (result === null) return
      GA4.sendEvent({ category: 'Chat Bot', action: 'Chat Bot Conversation Deleted' })
      setGroupedConversations((prev) => {
        const next: GroupedConversations = {}
        for (const [month, items] of Object.entries(prev)) {
          const filtered = items.filter((c) => c.conversationId !== id)
          if (filtered.length > 0) {
            next[month] = filtered
          }
        }
        return next
      })
      if (conversationIdFromUrl === id) {
        setIsChatOpen(false)
        setConversationMessage(null)
        navigate(window.location.pathname, { replace: true })
      }
    } catch (err) {
      console.error('Error deleting conversation:', err)
    }
  }, [conversationIdFromUrl, navigate])

  if (loading) { return <S.LoadContainer><Spinner variant="page" /></S.LoadContainer> }
  if (isCancelled) { return null }
  if (showTermsInFooter) { return <TermsAndConditions showTermsInFooter={showTermsInFooter} /> }

  return (
    <S.Container $collapsed={isSidebarCollapsed} >
      {hasLoadedTermsStatus && !chatbotTermsUpToDate && <TermsAndConditions showTermsInFooter={false} onCancelCallback={() => setIsCancelled(true)} />}

      <ConversationSidebar
        isSidebarCollapsed={isSidebarCollapsed}
        handleToggleSidebar={handleToggleSidebar}
        conversationIdFromUrl={conversationIdFromUrl}
        groupedConversations={groupedConversations}
        loading={loading}
        loadingMore={loadingMore}
        nextToken={nextToken}
        error={error}
        listConversations={listConversations}
        onSelectConversation={onSelectConversation}
        onDeleteConversation={onDeleteConversation}
        openNewChat={openNewChat}
      />

      {!isChatOpen
        ? <S.ChatHome remainingMessageCount={remainingMessageCount} handleToggleChats={handleToggleChats} />
        : (
          <Chatbot
            remainingMessageCount={remainingMessageCount}
            message={message}
            conversation={conversationMessage}
            loadingConversation={loadingConversation}
          />
        )
      }
    </S.Container>
  )
}

export default AIChatbot

const S = {
  LoadContainer: styled.div`
    display: flex;
    height: 100%;
    margin-left: 8px;
    background: ${color('chatbotBackground')};
    align-items: center;
    justify-content: center;
  `,
  Container: styled.div<{ $collapsed: boolean }>`
    display: grid;
    grid-template-columns: ${({ $collapsed }) => ($collapsed ? '3% 97%' : '20% 80%')};
    height: 100%;
    margin-left: 8px;
    background: ${color('chatbotBackground')};

    @media (${screenSize.small}) {
      margin-left: 0px;
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }
  `,
  ChatHome: styled(ChatHome)`
    @media (${screenSize.small}) {
      width: 375px;
    }
  `
}
